import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Col, Container, Form, Nav, Row } from 'react-bootstrap';
import { useHistory, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { jsPDF } from 'jspdf';
import { ButtonWithLoading } from '@spark/ui-components/components/Button';
import { Card, CardBody, CardTitle } from '@spark/ui-components/components/Card';
import { BorderedBottomTabs, CustomNavLink, NavItem } from '@spark/ui-components/components/Tabs';
import PageHeader from '@spark/ui-components/components/BreadCrumb';
import { H3 } from '@spark/ui-components/components/Typography';
import ConfirmDeleteModal from '@/containers/UI/Modals/index2';
import { notification } from '@spark/ui-components/utils/notification';
import { useAuth } from '@/context/AuthContext';
import {
  createMensagemOuvidoria,
  getFilialOuvidoriaById,
  createOuvidoriaMedida,
  deleteOuvidoria,
  getOuvidoriaById,
  updateOuvidoria
} from '@spark/services';
import OuvidoriaCadastro from '../Cadastro/index';

const formatDateTime = value => {
  if (!value) return '-';

  return new Date(value).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const isImageAnexo = anexo => {
  const tipo = String(anexo?.anexoTipo || '').toLowerCase();
  if (tipo.startsWith('image/')) return true;

  const url = String(anexo?.anexoUrl || '').toLowerCase();
  return (
    url.includes('.png') ||
    url.includes('.jpg') ||
    url.includes('.jpeg') ||
    url.includes('.webp') ||
    url.includes('.gif') ||
    url.includes('.bmp')
  );
};

const formatBool = value => {
  if (value === true) return 'Sim';
  if (value === false) return 'Nao';
  return '-';
};

const normalizeEnumText = value =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z]/g, '')
    .toLowerCase();

const parseStatusAtualToNumber = statusAtual => {
  if (statusAtual === undefined || statusAtual === null || statusAtual === '') return undefined;
  const numeric = Number(statusAtual);
  if (!Number.isNaN(numeric)) return numeric;

  const normalized = normalizeEnumText(statusAtual);
  const map = {
    emaberto: 1,
    visto: 2,
    emanalise: 3,
    emcomite: 4,
    finalizado: 5,
  };
  return map[normalized];
};

const parseRelatadoLiderToNumber = relatadoLiderStatus => {
  if (relatadoLiderStatus === undefined || relatadoLiderStatus === null || relatadoLiderStatus === '') return undefined;
  const numeric = Number(relatadoLiderStatus);
  if (!Number.isNaN(numeric)) return numeric;

  const normalized = normalizeEnumText(relatadoLiderStatus);
  const map = {
    sim: 1,
    nao: 2,
    prefironao: 3,
    naoseiqueme: 4,
  };
  return map[normalized];
};

const getUsernameFromProfile = profile => {
  if (!profile || typeof profile !== 'object') return '';

  return (
    profile?.username ||
    profile?.['cognito:username'] ||
    profile?.name ||
    profile?.email ||
    ''
  );
};

const readUsernameFromStorage = storage => {
  if (!storage) return '';

  try {
    const directProfile = storage.getItem('profile');
    if (directProfile) {
      const parsedDirect = JSON.parse(directProfile);
      const directUsername = getUsernameFromProfile(parsedDirect?.profile || parsedDirect);
      if (directUsername) return directUsername;
    }

    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index);
      if (!key) continue;

      const rawValue = storage.getItem(key);
      if (!rawValue || rawValue[0] !== '{') continue;

      try {
        const parsedValue = JSON.parse(rawValue);
        const candidateUsername = getUsernameFromProfile(parsedValue?.profile || parsedValue);
        if (candidateUsername) return candidateUsername;
      } catch {
        // Ignora valores que nao sao JSON valido.
      }
    }
  } catch (error) {
    console.error('Erro ao ler profile do storage:', error);
  }

  return '';
};

const getCurrentUsername = user => {
  const fromSession = readUsernameFromStorage(window?.sessionStorage);
  if (fromSession) return fromSession;

  const fromUser = getUsernameFromProfile(user?.profile || user);
  if (fromUser) return fromUser;

  return readUsernameFromStorage(window?.localStorage);
};

const OuvidoriaDetalhes = () => {
  const history = useHistory();
  const { user } = useAuth();
  const { id } = useParams();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletando, setDeletando] = useState(false);
  const [activeTab, setActiveTab] = useState('detalhes');
  const [ouvidoriaInfo, setOuvidoriaInfo] = useState(null);
  const [mensagens, setMensagens] = useState([]);
  const [anexos, setAnexos] = useState([]);
  const [medidas, setMedidas] = useState([]);
  const [loadingAnexos, setLoadingAnexos] = useState(false);
  const [loadingMensagens, setLoadingMensagens] = useState(false);
  const [loadingMedidas, setLoadingMedidas] = useState(false);
  const [mensagemTexto, setMensagemTexto] = useState('');
  const [enviandoMensagem, setEnviandoMensagem] = useState(false);
  const [medidaTomada, setMedidaTomada] = useState('');
  const [desconsiderado, setDesconsiderado] = useState(false);
  const [motivoDesconsiderado, setMotivoDesconsiderado] = useState('');
  const [enviandoMedida, setEnviandoMedida] = useState(false);
  const [salvandoComite, setSalvandoComite] = useState(false);
  const usuarioCriacao = useMemo(() => getCurrentUsername(user), [user]);
  const statusAtualNumero = useMemo(
    () => parseStatusAtualToNumber(ouvidoriaInfo?.statusAtual),
    [ouvidoriaInfo?.statusAtual]
  );
  const isOuvidoriaEmComite = statusAtualNumero === 4;
  const isOuvidoriaFinalizada = useMemo(
    () => statusAtualNumero === 5,
    [statusAtualNumero]
  );

  const loadOuvidoriaData = useCallback(async () => {
    if (!id) return;

    try {
      setLoadingMensagens(true);
      setLoadingAnexos(true);
      setLoadingMedidas(true);
      let ouvidoria = await getOuvidoriaById(id);

      if (ouvidoria?.filialId && !ouvidoria?.filial) {
        try {
          const filial = await getFilialOuvidoriaById(ouvidoria.filialId);
          if (filial && typeof filial === 'object') {
            ouvidoria = {
              ...ouvidoria,
              filial: {
                id: filial.id,
                nomeFilial: filial.nomeFilial || '',
                dataCriacao: filial.dataCriacao || null,
              },
            };
          }
        } catch (error) {
          console.error('Erro ao carregar dados da filial da ouvidoria:', error);
        }
      }

      const statusAtual = parseStatusAtualToNumber(ouvidoria?.statusAtual);
      if (ouvidoria?.motivoId && statusAtual === 1) {
        try {
          const updated = await updateOuvidoria(id, {
            motivoId: Number(ouvidoria.motivoId),
            relato: ouvidoria?.relato || '',
            desejaAcompanhar:
              typeof ouvidoria?.desejaAcompanhar === 'boolean' ? ouvidoria.desejaAcompanhar : null,
            relatadoLiderStatus: parseRelatadoLiderToNumber(ouvidoria?.relatadoLiderStatus),
            statusAtual: 2,
            protocolo: ouvidoria?.protocolo || '',
            telefone: ouvidoria?.telefone || '',
            filialId: ouvidoria?.filialId ?? null,
          });

          if (updated && typeof updated === 'object') {
            ouvidoria = updated;
          }
        } catch (error) {
          console.error('Erro ao marcar ouvidoria como vista:', error);
        }
      }

      const mensagensOuvidoria = Array.isArray(ouvidoria?.mensagens) ? ouvidoria.mensagens : [];
      const anexosOuvidoria = Array.isArray(ouvidoria?.anexos) ? ouvidoria.anexos : [];
      const medidasOuvidoria = Array.isArray(ouvidoria?.medidas) ? ouvidoria.medidas : [];
      const ordenadas = [...mensagensOuvidoria].sort((a, b) => {
        const dataA = a?.dataCriacao ? new Date(a.dataCriacao).getTime() : 0;
        const dataB = b?.dataCriacao ? new Date(b.dataCriacao).getTime() : 0;
        if (dataA !== dataB) return dataB - dataA;
        return Number(b?.id || 0) - Number(a?.id || 0);
      });
      const anexosOrdenados = [...anexosOuvidoria].sort((a, b) => {
        const dataA = a?.dataCriacao ? new Date(a.dataCriacao).getTime() : 0;
        const dataB = b?.dataCriacao ? new Date(b.dataCriacao).getTime() : 0;
        if (dataA !== dataB) return dataB - dataA;
        return Number(b?.id || 0) - Number(a?.id || 0);
      });
      const medidasOrdenadas = [...medidasOuvidoria].sort((a, b) => {
        const dataA = a?.criadoEm ? new Date(a.criadoEm).getTime() : 0;
        const dataB = b?.criadoEm ? new Date(b.criadoEm).getTime() : 0;
        if (dataA !== dataB) return dataB - dataA;
        return Number(b?.id || 0) - Number(a?.id || 0);
      });
      setMensagens(ordenadas);
      setAnexos(anexosOrdenados);
      setMedidas(medidasOrdenadas);
      setOuvidoriaInfo(ouvidoria || null);
    } catch (error) {
      console.error('Erro ao carregar mensagens da ouvidoria:', error);
      setMensagens([]);
      setAnexos([]);
      setMedidas([]);
      setOuvidoriaInfo(null);
    } finally {
      setLoadingMensagens(false);
      setLoadingAnexos(false);
      setLoadingMedidas(false);
    }
  }, [id]);

  useEffect(() => {
    loadOuvidoriaData();
  }, [loadOuvidoriaData]);

  const handleVoltar = useCallback(() => {
    history.goBack();
  }, [history]);

  const handleConfirmDelete = useCallback(async () => {
    try {
      setDeletando(true);
      await deleteOuvidoria(id);
      notification.success('Registro de ouvidoria excluido com sucesso!');
      history.push('/pages/ouvidoria');
    } catch (error) {
      console.error('Erro ao excluir registro de ouvidoria:', error);
    } finally {
      setDeletando(false);
      setShowDeleteModal(false);
    }
  }, [history, id]);

  const handleEnviarMensagem = useCallback(async () => {
    if (!id) return;
    if (isOuvidoriaFinalizada) {
      notification.error('Ouvidoria finalizada nao permite novas mensagens.');
      return;
    }

    const mensagem = String(mensagemTexto || '').trim();
    if (!mensagem) {
      notification.error('Digite uma mensagem antes de enviar.');
      return;
    }

    if (!usuarioCriacao) {
      notification.error('Nao foi possivel identificar o usuario logado.');
      return;
    }

    try {
      setEnviandoMensagem(true);
      await createMensagemOuvidoria({
        ouvidoriaId: Number(id),
        usuarioCriacao,
        mensagem,
        descricao: '',
      });

      setMensagemTexto('');
      await loadOuvidoriaData();
      notification.success('Mensagem enviada com sucesso!');
    } catch (error) {
      console.error('Erro ao enviar mensagem da ouvidoria:', error);
      notification.error('Nao foi possivel enviar a mensagem.');
    } finally {
      setEnviandoMensagem(false);
    }
  }, [id, isOuvidoriaFinalizada, loadOuvidoriaData, mensagemTexto, usuarioCriacao]);

  const handleEnviarMedida = useCallback(async () => {
    if (!id) return;
    if (isOuvidoriaFinalizada) {
      notification.error('Ouvidoria finalizada nao permite novas medidas.');
      return;
    }

    if (!usuarioCriacao) {
      notification.error('Nao foi possivel identificar o usuario logado.');
      return;
    }

    const textoMedida = String(medidaTomada || '').trim();
    const textoMotivo = String(motivoDesconsiderado || '').trim();

    if (desconsiderado) {
      if (!textoMotivo) {
        notification.error('Informe o motivo de desconsiderado.');
        return;
      }
    } else if (!textoMedida) {
      notification.error('Informe a medida tomada.');
      return;
    }

    try {
      setEnviandoMedida(true);
      await createOuvidoriaMedida({
        ouvidoriaId: Number(id),
        medidaTomada: desconsiderado ? '' : textoMedida,
        desconsiderado,
        motivoDesconsiderado: desconsiderado ? textoMotivo : '',
        usuarioFinalizado: usuarioCriacao,
      });

      setMedidaTomada('');
      setDesconsiderado(false);
      setMotivoDesconsiderado('');
      await loadOuvidoriaData();
      notification.success('Medida registrada com sucesso!');
    } catch (error) {
      console.error('Erro ao registrar medida da ouvidoria:', error);
      notification.error('Nao foi possivel registrar a medida.');
    } finally {
      setEnviandoMedida(false);
    }
  }, [desconsiderado, id, isOuvidoriaFinalizada, loadOuvidoriaData, medidaTomada, motivoDesconsiderado, usuarioCriacao]);

  const handleDefinirParaComite = useCallback(async checked => {
    if (!id || !ouvidoriaInfo) return;
    if (isOuvidoriaFinalizada) {
      notification.error('Registro finalizado nao pode ser alterado para comite.');
      return;
    }

    if (checked && isOuvidoriaEmComite) {
      return;
    }

    if (!checked && !isOuvidoriaEmComite) {
      return;
    }

    if (checked && !usuarioCriacao) {
      notification.error('Nao foi possivel identificar o usuario logado.');
      return;
    }

    const proximoStatus = checked ? 4 : (mensagens.length > 0 ? 3 : 2);

    try {
      setSalvandoComite(true);
      const updated = await updateOuvidoria(id, {
        motivoId: Number(ouvidoriaInfo?.motivoId || 0),
        relato: ouvidoriaInfo?.relato || '',
        desejaAcompanhar:
          typeof ouvidoriaInfo?.desejaAcompanhar === 'boolean'
            ? ouvidoriaInfo.desejaAcompanhar
            : null,
        relatadoLiderStatus: parseRelatadoLiderToNumber(ouvidoriaInfo?.relatadoLiderStatus),
        statusAtual: proximoStatus,
        protocolo: ouvidoriaInfo?.protocolo || '',
        telefone: ouvidoriaInfo?.telefone || '',
        filialId: ouvidoriaInfo?.filialId ?? null,
      });

      if (updated && typeof updated === 'object') {
        setOuvidoriaInfo(updated);
      }

      if (checked) {
        const mensagemComite = `O usuario ${usuarioCriacao} definiu o registro para o comite`;
        await createMensagemOuvidoria({
          ouvidoriaId: Number(id),
          usuarioCriacao,
          mensagem: mensagemComite,
          descricao: '',
        });
      }

      await loadOuvidoriaData();
      if (checked) {
        notification.success('Registro definido para o proximo comite.');
      } else {
        notification.success(
          proximoStatus === 3
            ? 'Registro removido do comite e definido como Em analise.'
            : 'Registro removido do comite e definido como Visto.'
        );
      }
    } catch (error) {
      console.error('Erro ao definir registro para comite:', error);
      notification.error('Nao foi possivel definir o registro para comite.');
    } finally {
      setSalvandoComite(false);
    }
  }, [id, isOuvidoriaEmComite, isOuvidoriaFinalizada, loadOuvidoriaData, mensagens.length, ouvidoriaInfo, usuarioCriacao]);

  const handleCopyAnexoUrl = useCallback(async anexoUrl => {
    const url = String(anexoUrl || '').trim();
    if (!url) {
      notification.error('URL do anexo indisponivel.');
      return;
    }

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = url;
        textArea.setAttribute('readonly', '');
        textArea.style.position = 'absolute';
        textArea.style.left = '-9999px';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }

      notification.success('URL do anexo copiada!');
    } catch (error) {
      console.error('Erro ao copiar URL do anexo:', error);
      notification.error('Nao foi possivel copiar a URL do anexo.');
    }
  }, []);

  const handleBaixarPdf = useCallback(() => {
    if (!ouvidoriaInfo) {
      notification.error('Dados da ouvidoria ainda nao foram carregados.');
      return;
    }

    const doc = new jsPDF({ unit: 'pt', format: 'a4' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 36;
    const contentWidth = pageWidth - margin * 2;
    const labelWidth = 118;
    let y = 106;

    const ensureSpace = (height = 18) => {
      if (y + height > pageHeight - margin) {
        doc.addPage();
        y = margin;
      }
    };

    const addSectionTitle = title => {
      ensureSpace(30);
      doc.setFillColor(240, 247, 255);
      doc.roundedRect(margin, y - 14, contentWidth, 22, 4, 4, 'F');
      doc.setTextColor(18, 111, 199);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11.5);
      doc.text(title, margin + 10, y);
      y += 24;
      doc.setTextColor(45, 55, 72);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
    };

    const addField = (label, value) => {
      const safeValue = String(value ?? '-');
      const lines = doc.splitTextToSize(safeValue, contentWidth - labelWidth - 16);
      ensureSpace(16 + (lines.length - 1) * 11);

      doc.setFont('helvetica', 'bold');
      doc.setTextColor(74, 85, 104);
      doc.text(`${label}:`, margin + 8, y);

      doc.setFont('helvetica', 'normal');
      doc.setTextColor(31, 41, 55);
      doc.text(lines, margin + 8 + labelWidth, y);

      y += 15 + (lines.length - 1) * 11;
    };

    const addParagraph = text => {
      const lines = doc.splitTextToSize(String(text || '-'), contentWidth - 16);
      ensureSpace(18 + (lines.length - 1) * 11);
      doc.setTextColor(31, 41, 55);
      doc.text(lines, margin + 8, y);
      y += 16 + (lines.length - 1) * 11;
    };

    const idOuvidoria = ouvidoriaInfo?.id || '-';
    const protocolo = ouvidoriaInfo?.protocolo || '-';
    const motivo = ouvidoriaInfo?.motivo?.nomeMotivo || '-';
    const filialNome = ouvidoriaInfo?.filial?.nomeFilial || '-';
    const filialId = ouvidoriaInfo?.filialId ?? ouvidoriaInfo?.filial?.id ?? '-';
    const tagsText = Array.isArray(ouvidoriaInfo?.tags) && ouvidoriaInfo.tags.length > 0
      ? ouvidoriaInfo.tags.map(tag => tag?.tag?.nomeTag || `Tag ${tag?.tagId || '-'}`).join(', ')
      : '-';

    doc.setFillColor(18, 111, 199);
    doc.rect(0, 0, pageWidth, 86, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(22);
    doc.text(`Registro de Ouvidoria #${idOuvidoria}`, margin, 38);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11.5);
    doc.text(`Protocolo: ${protocolo}`, margin, 60);
    doc.text(`Gerado em: ${formatDateTime(new Date())}`, pageWidth - margin, 60, { align: 'right' });

    addSectionTitle('Dados Gerais');
    addField('ID', idOuvidoria);
    addField('Protocolo', protocolo);
    addField('Motivo', motivo);
    addField('Filial', filialNome);
    addField('ID Filial', filialId);
    addField('Status atual', ouvidoriaInfo?.statusAtual || '-');
    addField('Relatado lider', ouvidoriaInfo?.relatadoLiderStatus || '-');
    addField('Deseja acompanhar', formatBool(ouvidoriaInfo?.desejaAcompanhar));
    addField('Data criacao', formatDateTime(ouvidoriaInfo?.dataCriacao));
    addField('Tags', tagsText);
    y += 12;

    addSectionTitle('Relato');
    addParagraph(ouvidoriaInfo?.relato || '-');
    y += 12;

    addSectionTitle('Mensagens');
    if (!Array.isArray(mensagens) || mensagens.length === 0) {
      addField('Info', 'Nenhuma mensagem registrada.');
    } else {
      mensagens.forEach((mensagem, index) => {
        ensureSpace(30);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(44, 82, 130);
        doc.text(
          `${index + 1}. ${mensagem?.usuarioCriacao || '-'} - ${formatDateTime(mensagem?.dataCriacao)}`,
          margin + 8,
          y
        );
        y += 16;
        addParagraph(mensagem?.mensagem || '-');
        if (mensagem?.descricao) {
          addField('Descricao', mensagem.descricao);
        }
        y += 8;
      });
    }

    const fileName = `ouvidoria_${idOuvidoria}_${protocolo}`.replace(/[^\w-]+/g, '_');
    doc.save(`${fileName}.pdf`);
  }, [mensagens, ouvidoriaInfo]);

  return (
    <Container fluid>
      <Row>
        <Col md={12}>
          <PageHeader
            title=""
            routeSegments={[
              { name: 'Inicio' },
              { name: 'Ouvidoria' },
              { name: 'Detalhes' },
            ]}
          />
        </Col>
      </Row>

      <Row>
        <Col md={12}>
          <Card>
            <CardBody>
              <HeaderWrap>
                <CardTitle>
                  <H3>Detalhes do Registro de Ouvidoria</H3>
                </CardTitle>
                <ButtonContainer>
                  <ButtonWithLoading variant="outline-secondary" onClick={handleVoltar}>
                    Voltar
                  </ButtonWithLoading>
                  <ButtonWithLoading variant="primary" onClick={handleBaixarPdf}>
                    Baixar PDF
                  </ButtonWithLoading>
                  <ButtonWithLoading
                    variant="outline-danger"
                    onClick={() => setShowDeleteModal(true)}
                  >
                    Excluir
                  </ButtonWithLoading>
                </ButtonContainer>
              </HeaderWrap>

              {isOuvidoriaFinalizada && (
                <StatusFinalizadoAlert>
                  Essa ouvidoria ja foi finalizada, portante nao e possivel interagir com o registro
                </StatusFinalizadoAlert>
              )}

              <RegistroMetaWrap>
                <RegistroMetaItem>
                  <RegistroMetaLabel>Filial</RegistroMetaLabel>
                  <RegistroMetaValue>{ouvidoriaInfo?.filial?.nomeFilial || '-'}</RegistroMetaValue>
                </RegistroMetaItem>
              </RegistroMetaWrap>

              <BorderedBottomTabs className="mb-3">
                <Nav variant="tabs" className="nav-tabs">
                  <NavItem>
                    <CustomNavLink
                      active={activeTab === 'detalhes'}
                      onClick={() => setActiveTab('detalhes')}
                    >
                      <span className="tab-text">Detalhes</span>
                    </CustomNavLink>
                  </NavItem>
                  <NavItem>
                    <CustomNavLink
                      active={activeTab === 'mensagens'}
                      onClick={() => setActiveTab('mensagens')}
                    >
                      <span className="tab-text">Mensagens</span>
                    </CustomNavLink>
                  </NavItem>
                  <NavItem>
                    <CustomNavLink
                      active={activeTab === 'anexos'}
                      onClick={() => setActiveTab('anexos')}
                    >
                      <span className="tab-text">Anexos</span>
                    </CustomNavLink>
                  </NavItem>
                  <NavItem>
                    <CustomNavLink
                      active={activeTab === 'medidas'}
                      onClick={() => setActiveTab('medidas')}
                    >
                      <span className="tab-text">Medidas</span>
                    </CustomNavLink>
                  </NavItem>
                </Nav>
              </BorderedBottomTabs>

              {activeTab === 'detalhes' && (
                <>
                  <ComiteToggleWrap>
                    <Form.Check
                      type="checkbox"
                      id="definir-comite-checkbox"
                      label="Definir esse registro para o proximo Comite"
                      checked={isOuvidoriaEmComite}
                      disabled={isOuvidoriaFinalizada || salvandoComite}
                      onChange={event => handleDefinirParaComite(event.target.checked)}
                    />
                    {salvandoComite && (
                      <MensagemEmpty>Salvando alteracao de status para comite...</MensagemEmpty>
                    )}
                  </ComiteToggleWrap>
                  <OuvidoriaCadastro isReadOnly={true} id={id} hideHeader={true} />
                </>
              )}

              {activeTab === 'mensagens' && (
                <MensagensWrap>
                  {isOuvidoriaFinalizada && (
                    <MensagemEmpty>Registro finalizado. Novas mensagens estao bloqueadas.</MensagemEmpty>
                  )}
                  <MensagemComposer>
                    <Form.Group className="mb-2">
                      <Form.Label>Mensagem</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        value={mensagemTexto}
                        onChange={event => setMensagemTexto(event.target.value)}
                        placeholder="Digite a mensagem a ser registrada..."
                        disabled={isOuvidoriaFinalizada}
                      />
                    </Form.Group>

                    <ButtonWithLoading
                      variant="primary"
                      onClick={handleEnviarMensagem}
                      loading={enviandoMensagem ? 'true' : undefined}
                      disabled={enviandoMensagem || isOuvidoriaFinalizada}
                    >
                      Enviar mensagem
                    </ButtonWithLoading>
                  </MensagemComposer>

                  <MensagemList>
                    {loadingMensagens && <MensagemEmpty>Carregando mensagens...</MensagemEmpty>}

                    {!loadingMensagens && mensagens.length === 0 && (
                      <MensagemEmpty>Nenhuma mensagem registrada para esta ouvidoria.</MensagemEmpty>
                    )}

                    {!loadingMensagens &&
                      mensagens.map(mensagem => (
                        <MensagemItem key={mensagem.id}>
                          <MensagemHeader>
                            <MensagemAuthor>{mensagem.usuarioCriacao || '-'}</MensagemAuthor>
                            <MensagemDate>{formatDateTime(mensagem.dataCriacao)}</MensagemDate>
                          </MensagemHeader>
                          <MensagemBody>{mensagem.mensagem || '-'}</MensagemBody>
                          {mensagem.descricao && (
                            <MensagemDescription>{mensagem.descricao}</MensagemDescription>
                          )}
                        </MensagemItem>
                      ))}
                  </MensagemList>
                </MensagensWrap>
              )}

              {activeTab === 'anexos' && (
                <AnexosWrap>
                  <AnexoList>
                    {loadingAnexos && <MensagemEmpty>Carregando anexos...</MensagemEmpty>}

                    {!loadingAnexos && anexos.length === 0 && (
                      <MensagemEmpty>Nenhum anexo registrado para esta ouvidoria.</MensagemEmpty>
                    )}

                    {!loadingAnexos &&
                      anexos.map(anexo => (
                        <AnexoItem key={anexo.id}>
                          <AnexoHeader>
                            <MensagemAuthor>{anexo.anexoTipo || 'Arquivo'}</MensagemAuthor>
                            <MensagemDate>{formatDateTime(anexo.dataCriacao)}</MensagemDate>
                          </AnexoHeader>
                          {anexo.anexoUrl ? (
                            <>
                              {isImageAnexo(anexo) && (
                                <AnexoImage
                                  src={anexo.anexoUrl}
                                  alt={`Anexo ${anexo.id}`}
                                  loading="lazy"
                                />
                              )}
                              <AnexoCopyButton
                                type="button"
                                onClick={() => handleCopyAnexoUrl(anexo.anexoUrl)}
                                title="Copiar URL do anexo"
                              >
                                <CopyIcon aria-hidden="true">⎘</CopyIcon>
                                <span>Copia URL</span>
                              </AnexoCopyButton>
                            </>
                          ) : (
                            <MensagemEmpty>URL do anexo indisponivel.</MensagemEmpty>
                          )}
                        </AnexoItem>
                      ))}
                  </AnexoList>
                </AnexosWrap>
              )}

              {activeTab === 'medidas' && (
                <MedidasWrap>
                  {isOuvidoriaFinalizada && (
                    <MensagemEmpty>Registro finalizado. Novas medidas estao bloqueadas.</MensagemEmpty>
                  )}
                  <MedidaComposer>
                    <Form.Group className="mb-2">
                      <Form.Label>Medida tomada</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        value={medidaTomada}
                        onChange={event => setMedidaTomada(event.target.value)}
                        placeholder="Descreva a medida tomada pela empresa..."
                        disabled={desconsiderado || isOuvidoriaFinalizada}
                      />
                    </Form.Group>

                    <Form.Group className="mb-2">
                      <Form.Check
                        type="checkbox"
                        id="desconsiderado-checkbox"
                        label="Desconsiderar"
                        checked={desconsiderado}
                        disabled={isOuvidoriaFinalizada}
                        onChange={event => {
                          const checked = event.target.checked;
                          setDesconsiderado(checked);
                          if (checked) {
                            setMedidaTomada('');
                          } else {
                            setMotivoDesconsiderado('');
                          }
                        }}
                      />
                    </Form.Group>

                    <Form.Group className="mb-2">
                      <Form.Label>Motivo desconsiderado</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        value={motivoDesconsiderado}
                        onChange={event => setMotivoDesconsiderado(event.target.value)}
                        placeholder="Informe o motivo da desconsideracao..."
                        disabled={!desconsiderado || isOuvidoriaFinalizada}
                      />
                    </Form.Group>

                    <ButtonWithLoading
                      variant="primary"
                      onClick={handleEnviarMedida}
                      loading={enviandoMedida ? 'true' : undefined}
                      disabled={enviandoMedida || isOuvidoriaFinalizada}
                    >
                      Registrar medida
                    </ButtonWithLoading>
                  </MedidaComposer>

                  <MedidaList>
                    {loadingMedidas && <MensagemEmpty>Carregando medidas...</MensagemEmpty>}

                    {!loadingMedidas && medidas.length === 0 && (
                      <MensagemEmpty>Nenhuma medida registrada para esta ouvidoria.</MensagemEmpty>
                    )}

                    {!loadingMedidas &&
                      medidas.map(medida => (
                        <MedidaItem key={medida.id}>
                          <MensagemHeader>
                            <MensagemAuthor>{medida.usuarioFinalizado || '-'}</MensagemAuthor>
                            <MensagemDate>{formatDateTime(medida.criadoEm)}</MensagemDate>
                          </MensagemHeader>
                          <MedidaStatus>
                            {medida.desconsiderado ? 'Desconsiderado: Sim' : 'Desconsiderado: Nao'}
                          </MedidaStatus>
                          <MensagemBody>{medida.medidaTomada || '-'}</MensagemBody>
                          {medida.desconsiderado && medida.motivoDesconsiderado && (
                            <MensagemDescription>{medida.motivoDesconsiderado}</MensagemDescription>
                          )}
                        </MedidaItem>
                      ))}
                  </MedidaList>
                </MedidasWrap>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      <ConfirmDeleteModal
        show={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleConfirmDelete}
        loading={deletando}
        title="Excluir Registro de Ouvidoria"
        message="Tem certeza que deseja excluir este registro de ouvidoria? Esta acao nao pode ser desfeita."
      />
    </Container>
  );
};

export default OuvidoriaDetalhes;

const HeaderWrap = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
`;

const StatusFinalizadoAlert = styled.div`
  margin-bottom: 16px;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid #f3c56a;
  background: #fff7e6;
  color: #8a5b00;
  font-size: 13px;
  font-weight: 600;
`;

const RegistroMetaWrap = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 14px;
  flex-wrap: wrap;
`;

const RegistroMetaItem = styled.div`
  min-width: 220px;
  border: 1px solid #d9e1ec;
  border-radius: 8px;
  padding: 10px 12px;
  background: #f8fafc;
`;

const RegistroMetaLabel = styled.div`
  font-size: 12px;
  color: #667085;
  margin-bottom: 4px;
  font-weight: 600;
`;

const RegistroMetaValue = styled.div`
  font-size: 14px;
  color: #1f2937;
  font-weight: 600;
`;

const ComiteToggleWrap = styled.div`
  display: flex;
  align-items: center;
  margin: 6px 0 14px;
  padding: 10px 12px;
  border: 1px solid #d9e1ec;
  border-radius: 8px;
  background: #f8fafc;

  .form-check {
    display: flex;
    align-items: center;
    padding-left: 0;
    margin: 0;
    min-height: 24px;
  }

  .form-check-input {
    position: static;
    float: none;
    flex-shrink: 0;
    margin: 0 8px 0 0;
    margin-top: 0;
  }

  .form-check-label {
    display: inline-flex;
    align-items: center;
    margin: 0;
    line-height: 1.2;
  }
`;

const MensagensWrap = styled.div`
  margin-top: 8px;
`;

const AnexosWrap = styled.div`
  margin-top: 8px;
`;

const MedidasWrap = styled.div`
  margin-top: 8px;
`;

const MensagemComposer = styled.div`
  margin-top: 10px;
  margin-bottom: 18px;
`;

const MensagemList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const MedidaList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const MensagemItem = styled.div`
  border: 1px solid #d9e1ec;
  border-left: 4px solid #3f6ad8;
  border-radius: 10px;
  padding: 14px 16px;
  background: #ffffff;
  box-shadow: 0 2px 6px rgba(17, 24, 39, 0.06);
`;

const MensagemHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
  flex-wrap: wrap;
`;

const MensagemAuthor = styled.strong`
  font-size: 13px;
  font-weight: 600;
  color: #1f2d3d;
`;

const MensagemDate = styled.span`
  font-size: 12px;
  color: #4a5568;
  background: #f2f5f9;
  border: 1px solid #d8e0ea;
  border-radius: 999px;
  padding: 2px 10px;
`;

const MensagemBody = styled.div`
  font-size: 14px;
  line-height: 1.5;
  color: #1f2937;
  white-space: pre-wrap;
`;

const MensagemDescription = styled.div`
  font-size: 12px;
  color: #5f6b7a;
  margin-top: 10px;
  padding-top: 8px;
  border-top: 1px dashed #d9e1ec;
`;

const MedidaStatus = styled.div`
  font-size: 12px;
  color: #4a5568;
  margin-bottom: 8px;
`;

const MensagemEmpty = styled.div`
  font-size: 13px;
  color: #6c757d;
  padding: 8px 0;
`;

const AnexoList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const MedidaComposer = styled.div`
  margin-top: 10px;
  margin-bottom: 18px;
`;

const AnexoItem = styled.div`
  border: 1px solid #d9e1ec;
  border-left: 4px solid #2c7a7b;
  border-radius: 10px;
  padding: 14px 16px;
  background: #ffffff;
  box-shadow: 0 2px 6px rgba(17, 24, 39, 0.06);
`;

const MedidaItem = styled.div`
  border: 1px solid #d9e1ec;
  border-left: 4px solid #2f855a;
  border-radius: 10px;
  padding: 14px 16px;
  background: #ffffff;
  box-shadow: 0 2px 6px rgba(17, 24, 39, 0.06);
`;

const AnexoHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
  flex-wrap: wrap;
`;

const AnexoCopyButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #6c757d;
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
  transition: color 0.2s ease;

  &:hover {
    color: #495057;
    text-decoration: underline;
  }
`;

const CopyIcon = styled.span`
  font-size: 14px;
  line-height: 1;
`;

const AnexoImage = styled.img`
  display: block;
  width: 100%;
  max-width: 460px;
  max-height: 360px;
  object-fit: contain;
  border: 1px solid #d8e0ea;
  border-radius: 8px;
  background: #f8fafc;
`;
